import React, { useCallback, useEffect, useState } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Form, FormGroup, Input, Label, Table } from "reactstrap";
import Loading from "../components/Loading";
import { getOrganisation } from "../utils/apiService";
import {
  ACTIVATION_STATE,
  hasLocation,
  readOfferLinks,
  readOrgOffers,
  readPlatformServices,
  saveOffer,
  setPublished,
} from "../utils/offersApi";

/** The homepage categories: platform service ids come in blocks of 100 per category (api/WidebySeed.cs). */
const CATEGORY_NAMES = {
  100: "Home & Garden",
  200: "Wellness & Beauty",
  300: "Automotive",
  400: "Food & Drink",
  500: "Fitness",
  600: "Repairs & Maintenance",
};
export const categoryOf = (serviceId) => CATEGORY_NAMES[Math.floor(serviceId / 100) * 100] || "Other";

const EMPTY_FORM = { serviceFulfilmentConfigId: 0, serviceId: "", name: "", price: "", durationMinutes: "30", isActive: ACTIVATION_STATE.INACTIVE };

export const OrganisationOffersComponent = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [org, setOrg] = useState(null);
  const [services, setServices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(null);

  const token = useCallback(() => getAccessTokenSilently().catch(() => null), [getAccessTokenSilently]);

  const load = useCallback(async () => {
    setError("");
    try {
      const t = await token();
      const [data, platformServices, orgOffers] = await Promise.all([
        getOrganisation(id, t),
        readPlatformServices(t),
        readOrgOffers(id, t),
      ]);
      setOrg({
        orgId: Number(id),
        name: data?.name || data?.orgName || data?.organisationName || "Unknown",
        latitude: data?.latitude || 0,
        longitude: data?.longitude || 0,
      });
      setServices(platformServices.sort((a, b) => a.serviceId - b.serviceId));
      setOffers(orgOffers);
    } catch (err) {
      setError(err?.message || "Failed to load offers");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    load();
  }, [load]);

  const serviceName = (serviceId) => services.find((s) => s.serviceId === serviceId)?.name || `Service ${serviceId}`;

  const run = async (key, action, done) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await action(await token());
      setNotice(done);
      await load();
      return true;
    } catch (err) {
      setError(err?.message || "Something went wrong");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const edit = async (offer) => {
    setError("");
    try {
      const links = await readOfferLinks(org.orgId, offer.serviceFulfilmentConfigId, await token());
      setForm({
        serviceFulfilmentConfigId: offer.serviceFulfilmentConfigId,
        serviceId: String(offer.serviceId),
        name: offer.name,
        price: String(offer.monetaryAmount),
        durationMinutes: String(links[0]?.durationMinutes || 30),
        isActive: offer.isActive,
      });
    } catch (err) {
      setError(err?.message || "Failed to load the offer");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.serviceId || !form.name.trim()) {
      setError("Choose a service and give the offer a name.");
      return;
    }
    const isNew = !form.serviceFulfilmentConfigId;
    const saved = await run("save",
      (t) => saveOffer(org, { ...form, serviceId: Number(form.serviceId), name: form.name.trim() }, t),
      isNew ? "Offer saved as a draft. Publish it to show it on the deals map." : "Offer saved.");
    if (saved) setForm(EMPTY_FORM);
  };

  const togglePublished = (offer, published) =>
    run(`publish-${offer.serviceFulfilmentConfigId}`, (t) => setPublished(org, offer, published, t),
      published ? `"${offer.name}" is published and shows on the deals map.` : `"${offer.name}" is no longer shown on the map.`);

  if (loading) return <Loading />;

  const located = org && hasLocation(org);
  const groupedServices = services.reduce((groups, s) => {
    const name = categoryOf(s.serviceId);
    (groups[name] = groups[name] || []).push(s);
    return groups;
  }, {});

  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Offers{org ? ` — ${org.name}` : ""}</h1>
        <Button color="secondary" onClick={() => history.push(`/organisations/${id}`)}>
          Back to organisation
        </Button>
      </div>

      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {org && !located && (
        <Alert color="warning">
          Add your business location first: customers find offers by where you are.{" "}
          <a href="#edit" onClick={(e) => { e.preventDefault(); history.push(`/organisations/${id}/edit`); }}>Set location</a>
        </Alert>
      )}

      <Card className="mb-4">
        <CardHeader>{form.serviceFulfilmentConfigId ? "Edit offer" : "New offer"}</CardHeader>
        <CardBody>
          <Form onSubmit={save}>
            <fieldset disabled={!located || busy !== null}>
              <FormGroup>
                <Label for="offerService">Service</Label>
                <Input id="offerService" type="select" value={form.serviceId}
                  onChange={(e) => setForm({ ...form, serviceId: e.target.value })}>
                  <option value="">Choose what you offer…</option>
                  {Object.entries(groupedServices).map(([category, list]) => (
                    <optgroup key={category} label={category}>
                      {list.map((s) => <option key={s.serviceId} value={s.serviceId}>{s.name}</option>)}
                    </optgroup>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label for="offerName">Offer name</Label>
                <Input id="offerName" value={form.name} placeholder="e.g. Lunch deal, Haircut and wash"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormGroup>
              <div className="d-flex">
                <FormGroup className="mr-3">
                  <Label for="offerPrice">Price (SEK)</Label>
                  <Input id="offerPrice" type="number" min="0" step="1" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label for="offerDuration">Length (minutes)</Label>
                  <Input id="offerDuration" type="number" min="5" step="5" value={form.durationMinutes}
                    onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
                </FormGroup>
              </div>
              <Button color="primary" type="submit">
                {busy === "save" ? "Saving…" : form.serviceFulfilmentConfigId ? "Save offer" : "Create offer"}
              </Button>{" "}
              {form.serviceFulfilmentConfigId !== 0 && (
                <Button color="link" type="button" onClick={() => setForm(EMPTY_FORM)}>Cancel</Button>
              )}
            </fieldset>
          </Form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>Your offers</CardHeader>
        <CardBody>
          {offers.length === 0 ? (
            <p className="mb-0">No offers yet. Create one above, then publish it to appear on the Wideby deals map.</p>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>Service</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => {
                  const live = o.isActive === ACTIVATION_STATE.ACTIVE;
                  const key = `publish-${o.serviceFulfilmentConfigId}`;
                  return (
                    <tr key={o.serviceFulfilmentConfigId}>
                      <td>{o.name}</td>
                      <td>{serviceName(o.serviceId)} <small className="text-muted">({categoryOf(o.serviceId)})</small></td>
                      <td>{o.monetaryAmount} SEK</td>
                      <td>{live ? <Badge color="success">Published</Badge> : <Badge color="secondary">Draft</Badge>}</td>
                      <td className="text-right">
                        <Button size="sm" color="link" disabled={busy !== null} onClick={() => edit(o)}>Edit</Button>
                        {live ? (
                          <Button size="sm" outline color="secondary" disabled={busy !== null} onClick={() => togglePublished(o, false)}>
                            {busy === key ? "…" : "Unpublish"}
                          </Button>
                        ) : (
                          <Button size="sm" color="success" disabled={busy !== null || !located} onClick={() => togglePublished(o, true)}>
                            {busy === key ? "Publishing…" : "Publish"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default withAuthenticationRequired(OrganisationOffersComponent, {
  onRedirecting: () => <Loading />,
});
